"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, User, Folder } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardTitle, CardFooter } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="224098" className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="h-[400px] animate-pulse bg-card border border-border shadow-none">
            <div className="h-48 bg-muted w-full rounded-t-none" />
            <CardContent className="p-6">
              <div className="h-4 bg-muted w-1/4 mb-4 rounded-none" />
              <div className="h-6 bg-muted w-3/4 mb-2 rounded-none" />
              <div className="h-6 bg-muted w-1/2 rounded-none" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        <p className="font-sans">Failed to load research publications. Please try again later.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p className="font-sans">No publications found in this category.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="flex flex-col h-full border border-border shadow-none bg-card overflow-hidden rounded-none transition-all duration-300 hover:-translate-y-1 hover:border-primary/50">
          {post.featuredImage && <Link to={post.link || "#"} className="block overflow-hidden h-48 shrink-0">
              <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 grayscale hover:grayscale-0" />
            </Link>}
          
          <CardContent className="flex-1 p-6 flex flex-col">
            <div className="flex items-center gap-2 text-[12px] text-primary font-mono uppercase tracking-wider mb-3">
              <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
              <time dateTime={post.date}>{post.date || "Published"}</time>
            </div>
            
            <Link to={post.link || "#"} className="group">
              <CardTitle className="font-serif text-xl font-bold text-foreground group-hover:text-primary transition-colors line-clamp-3 leading-tight">
                {post.title}
              </CardTitle>
            </Link>
            
            {post.excerpt && <div className="mt-3 text-muted-foreground font-default text-sm line-clamp-2 leading-relaxed" dangerouslySetInnerHTML={{
          __html: post.excerpt
        }} />}
          </CardContent>

          <CardFooter className="px-6 py-4 border-t border-border flex items-center justify-between text-[12px] text-muted-foreground font-mono bg-secondary/30 mt-auto">
            <div className="flex items-center gap-1.5">
              <User className="size-3.5" />
              <span className="truncate max-w-[100px] uppercase">{post.author?.name || "CAN Research"}</span>
            </div>
            
            {post.categories && post.categories.length > 0 && <div className="flex items-center gap-1.5">
                <Folder className="size-3.5" />
                <span className="truncate max-w-[100px] uppercase">{post.categories[0].name}</span>
              </div>}
          </CardFooter>
        </Card>)}
    </div>;
}
export default function PerformanceInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="publications-overview" className="dark bg-background text-foreground py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <div className="inline-block px-3 py-1 mb-4 border border-primary/30 text-primary font-mono text-xs uppercase tracking-widest">
            Research & Analysis
          </div>
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-foreground mb-4 tracking-tighter">Publications&nbsp; &amp; Commentary Overview</h2>
          <p className="font-default text-muted-foreground max-w-2xl mx-auto text-lg">
            Data-driven insights and policy recommendations designed to empower citizens and foster responsive governance.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <PostsGridConsumer />
        </PostsProvider>

        <div className="mt-12 text-center">
          <Link to="/Blog/" className="inline-flex items-center justify-center px-8 py-3 border border-primary text-primary font-mono text-sm uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-all duration-300">
            View All Publications
          </Link>
        </div>
      </div>
    </section>;
}